import Link from "next/link";
import type { ComponentProps } from "react";
import React from "react";
import { toast } from "react-toastify";
import { ChatPopContainer } from "~/components/common/chat/ChatPopContainer";
import { DealConstructionScheduleClientCard } from "~/components/common/deals/show/construction_card/DealConstructionScheduleClientCard";
import { DealCard } from "~/components/common/deals/show/deal_card/DealCard";
import { DealVendorNoteCard } from "~/components/common/deals/show/deal_card/DealVendorNoteCard";
import { DealInspectionCandidateDateCard } from "~/components/common/deals/show/inspection_candidate_date_card/DealInspectionCandidateDateCard";
import { DealMain } from "~/components/common/deals/show/main/DealMain";
import { DealRepairCard } from "~/components/common/deals/show/repair_card/DealRepairCard";
import { DealOriginVendorClientCard } from "~/components/common/deals/show/vendor_card/DealOriginVendorClientCard";
import { DealVisitDateClientCard } from "~/components/common/deals/show/visit_date_card/DealVisitDateClientCard";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loader from "~/components/common/parts/loading/Loading";
import { SERVER_ERROR_MESSAGE } from "~/constants/AppConst";
import { useMessages } from "~/hooks/deals/useMessages";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import useItandiAccount from "~/hooks/useItandiAccount";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import { UPDATE_TEMPORARY_UNREAD_MESSAGE } from "~/models/Message";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { isSuccess } from "~/utils/api/api_handler";
import { postTemporaryUnread } from "~/utils/api/deals/Message";
import Styles from "./Page.module.scss";

export const Page: React.FC = () => {
	const id = useIdQueryHandler();
	const { isOpen, handleClose, handleOpen } = useOpenHandler();
	const { data: deal, mutate: mutateDeal, isLoading } = useDeals().show(id);
	const { data: messages, mutate: mutateMessages } = useMessages().index(id);
	const { data: account } = useItandiAccount().currentAccount();
	const handleToggleChat = React.useCallback(() => {
		if (isOpen) {
			handleClose();
		} else {
			handleOpen();
		}
	}, [isOpen, handleClose, handleOpen]);
	const handleMutate = React.useCallback(async () => {
		await mutateDeal?.();
	}, [mutateDeal]);
	const handleMutateMessage = React.useCallback(async () => {
		await mutateMessages?.();
		await mutateDeal?.();
	}, [mutateDeal, mutateMessages]);
	if (isLoading || deal === null || account === null) {
		return <Loader isLoading />;
	}
	const handleTemporaryUnread = async (): Promise<void> => {
		const result = await postTemporaryUnread(deal.id);
		if (isSuccess(result)) {
			toast.success(UPDATE_TEMPORARY_UNREAD_MESSAGE);
			await mutateDeal?.();
			handleClose();
			return;
		}
		toast.error(SERVER_ERROR_MESSAGE);
	};
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ label: "案件詳細" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	return (
		<ItandiAccountContext.Provider value={account}>
			<DefaultV2>
				<ClientDealLayout deal={deal} items={items} noBackground>
					<div className={Styles.Container}>
						<div className={Styles.LeftCol}>
							<DealOriginVendorClientCard handleToggleChat={handleToggleChat} />
							<DealVendorNoteCard deal={deal} />
							<DealCard
								deal={deal}
								handleMutate={handleMutate}
								isVendor={false}
							/>
							{deal.dealType === "repair" && (
								<DealRepairCard
									companyType="client"
									deal={deal}
									handleMutate={handleMutate}
								/>
							)}
							<DealVisitDateClientCard />
							<DealInspectionCandidateDateCard
								deal={deal}
								handleMutate={handleMutate}
							/>
							<DealConstructionScheduleClientCard />
						</div>
						<DealMain deal={deal} isAdmin={account.isAdmin} isVendor={false} />
					</div>
				</ClientDealLayout>
				{messages !== null && (
					<ChatPopContainer
						deal={deal}
						handleClose={handleClose}
						handleMutate={handleMutateMessage}
						handleTemporaryUnread={handleTemporaryUnread}
						isAdmin={account.isAdmin}
						isOpen={isOpen}
						messages={messages}
						receiver="maintenance_company"
					/>
				)}
			</DefaultV2>
		</ItandiAccountContext.Provider>
	);
};
